import { useEffect, useMemo, useRef } from 'react';
import { FormikProps } from 'formik';
import { RULES_CONSTANT } from 'constant/RuleConstant';
import { useEvaluateDisplayTableRules } from 'services/v1/Tenant/AROKMS/DisplayTableService';
import { Field, RuleEvaluationResult } from 'types/api/Tenant/AROKMS/DisplayTableTypes';
import { getErrorMessage } from 'utils/Error';

interface RuleEvaluationListenerProps {
  formik: FormikProps<any>;
  fields: Field[];
  subjectId?: string | number;
  recordId?: string;
}

export const RULE_EVALUATION_LOADING_STATUS = 'ruleEvaluationLoading';
export const RULE_EVALUATION_PENDING_STATUS = 'ruleEvaluationPending';

export function isRuleEvaluationField(field: Field) {
  return field.serverEvaluated === true && Boolean(field.dependencyTableDefinitionIds?.length);
}

export function normalizeRuleEvaluationValue(value: any) {
  if (value && typeof value === 'object' && 'value' in value) {
    return value.value;
  }
  return value;
}

export function buildRuleEvaluationData(values: { [key: string]: any }) {
  return Object.entries(values).reduce<{ [key: string]: any }>((result, [columnName, value]) => {
    result[columnName] = normalizeRuleEvaluationValue(value);
    return result;
  }, {});
}

function valuesEqual(first: any, second: any) {
  return JSON.stringify(normalizeRuleEvaluationValue(first)) === JSON.stringify(normalizeRuleEvaluationValue(second));
}

export function getChangedTableDefinitionIds(
  previousValues: { [key: string]: any },
  currentValues: { [key: string]: any },
  fields: Field[]
) {
  const fieldsByName = new Map(fields.map((field) => [field.name, field]));
  const changedIds = new Set<string>();

  Object.keys({ ...previousValues, ...currentValues }).forEach((columnName) => {
    const field = fieldsByName.get(columnName);
    if (
      !field?.tableDefinitionId ||
      field.serverEvaluated === true ||
      field.ruleTypeCode === RULES_CONSTANT.TEXT.RULE_TEXT_PREFERENCE ||
      valuesEqual(previousValues[columnName], currentValues[columnName])
    ) {
      return;
    }

    changedIds.add(field.tableDefinitionId);
  });

  return Array.from(changedIds);
}

export function getEvaluationDisplayValue(evaluation: RuleEvaluationResult) {
  if (evaluation.status === 'EMPTY') return '';
  return evaluation.displayValue ?? evaluation.value ?? '';
}

export function getEvaluationFormValue(field: Field, evaluation: RuleEvaluationResult) {
  const displayValue = getEvaluationDisplayValue(evaluation);
  if (evaluation.status === 'EMPTY' || evaluation.value === null || evaluation.value === undefined) {
    return '';
  }

  if (field.type === 'dropdown') {
    return {
      label: displayValue,
      value: evaluation.value,
    };
  }

  return displayValue;
}

function formValuesEqual(first: any, second: any) {
  return JSON.stringify(first) === JSON.stringify(second);
}

export function RuleEvaluationListener({
  formik,
  fields,
  subjectId,
  recordId,
}: RuleEvaluationListenerProps) {
  const previousValuesRef = useRef(formik.values);
  const requestSequenceRef = useRef(0);
  const { mutateAsync: evaluateRules, isLoading } = useEvaluateDisplayTableRules(subjectId);
  const evaluationFields = useMemo(
    () => fields.filter(isRuleEvaluationField),
    [fields]
  );

  useEffect(() => {
    const currentLoadingStatus = formik.status?.[RULE_EVALUATION_LOADING_STATUS];
    if (currentLoadingStatus === isLoading || (!isLoading && currentLoadingStatus === undefined)) return;

    formik.setStatus({
      ...(formik.status || {}),
      [RULE_EVALUATION_LOADING_STATUS]: isLoading,
    });
  }, [formik, isLoading]);

  useEffect(() => {
    const previousValues = previousValuesRef.current;
    previousValuesRef.current = formik.values;

    if (!subjectId || evaluationFields.length === 0) return;

    const changedTableDefinitionIds = getChangedTableDefinitionIds(previousValues, formik.values, fields);
    if (changedTableDefinitionIds.length === 0) return;

    const requestSequence = ++requestSequenceRef.current;
    const requestData = buildRuleEvaluationData(formik.values);

    const evaluationTimer = window.setTimeout(() => {
      evaluateRules({
        ...(recordId ? { recordId } : {}),
        changedTableDefinitionIds,
        data: requestData,
      })
        .then((response) => {
          if (requestSequence !== requestSequenceRef.current) return;

          const pendingFields = {
            ...(formik.status?.[RULE_EVALUATION_PENDING_STATUS] || {}),
          };
          response.data.evaluations.forEach((evaluation) => {
            const targetField =
              fields.find((field) => field.tableDefinitionId === evaluation.tableDefinitionId) ||
              fields.find((field) => field.name === evaluation.columnName);

            if (!targetField) return;

            if (evaluation.status === 'PENDING_KTREE') {
              pendingFields[targetField.name] = true;
              formik.setFieldError(targetField.name, undefined);
              return;
            }

            delete pendingFields[targetField.name];

            const evaluatedValue = getEvaluationFormValue(targetField, evaluation);
            if (!formValuesEqual(formik.values[targetField.name], evaluatedValue)) {
              formik.setFieldValue(targetField.name, evaluatedValue, false);
            }
            formik.setFieldError(targetField.name, undefined);
          });
          formik.setStatus({
            ...(formik.status || {}),
            [RULE_EVALUATION_PENDING_STATUS]: pendingFields,
          });
        })
        .catch((error) => {
          if (requestSequence !== requestSequenceRef.current) return;

          const errorMessage = getErrorMessage(error);
          evaluationFields.forEach((field) => {
            formik.setFieldError(field.name, errorMessage);
            formik.setFieldTouched(field.name, true, false);
          });
        });
    }, 250);

    return () => window.clearTimeout(evaluationTimer);
    // Formik values are the event source; the remaining references are stable for a mounted form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values]);

  return null;
}
